/**
 * Cancel/pause subscription flow -- 5-step retention funnel.
 * Steps: Usage Summary -> Reason -> Sub-question -> Offers -> Result
 */
"use client";

import { useState, useCallback, useMemo, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { Img } from "@/components/shared/img";
import { Button } from "@speakai/ui/button";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@speakai/ui/dialog";
import { Badge } from "@speakai/ui/badge";
import { Skeleton } from "@speakai/ui/skeleton";
import { RadioGroup } from "@speakai/ui/radio-group";
import { Textarea } from "@speakai/ui/input";
import { Combobox } from "@/components/shared/combobox";
import { useToast } from "@speakai/ui/toast";
import { ExternalLink, Bot, FileText, Check, ClipboardList, Pause, MessageCircle, CircleCheckBig, CircleX, Calendar } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils/cn";
import { useTranslation } from "react-i18next";
import { trackFunnel } from "@/lib/integrations/sentry-tracking";
import { pushDataLayer } from "@/lib/integrations/ga4";
import { useCancelSubscription, usePauseSubscription, useAcceptOffer } from "../mutations";
import { useUsageAnalytics } from "../payment-queries";
import { useCredits, formatCredits } from "../hooks/use-credits";
import { PaymentSourceType, SubscriptionPlanId, SubscriptionStatus } from "../types";
import type { UserSubscription } from "../types";
import { useIntercom } from "@/hooks/use-intercom";
import { useAuth } from "@/hooks/use-auth";
import { customerSessionUrl, withBookingUtms } from "@/lib/utils/calendly";
import { HIGH_VALUE_COST_THRESHOLD, HIGH_VALUE_YEARLY_THRESHOLD } from "@/features/pricing/pricing-data";

// -- Types ---------------------------------------------------------
interface Suggestion { key: string; value: string; offerBundleKey: string }
interface CancellationReason { id: string | number; label: string; question: string; suggestions: Suggestion[] }
interface OfferDefinition {
  name: string; label: string; labelKey?: string; description?: string; desc?: string; descKey?: string; offer?: string;
  imagePath?: string; link?: string; isCredit?: boolean; creditAmount?: number; highCreditAmount?: number;
}

interface UsageAnalyticsData {
  media?: { media?: number; hours?: number };
  recorder?: { recorders?: number; recordings?: number };
  meetingAssistants?: { meetingAssistants?: number; calendars?: number };
  chat?: { totalChat?: number };
  automations?: number;
}

const CALENDLY_CANCEL_URL = customerSessionUrl("cancellation-session");

// -- Cancellation Reasons (10) --
const s = (key: string, value: string, bundle: string): Suggestion => ({ key, value, offerBundleKey: bundle });

const CANCELLATION_REASONS: CancellationReason[] = [
  {
    id: 1, label: "Impacted by market conditions", question: "What best describes your situation?", suggestions: [
      s("1", "Laid off from job", "commonOffers"),
      s("2", "Company restructuring", "commonOffers"),
      s("other", "Other (please provide)", "commonOffers"),
    ]
  },
  {
    id: 2, label: "We intend to use a different tool", question: "Which alternative product are you using now?", suggestions: [
      s("otter", "Otter.ai", "alternativeOffers"),
      s("fireflies", "Fireflies.ai", "alternativeOffers"),
      s("read", "Read.ai", "alternativeOffers"),
      s("descript", "Descript", "alternativeOffers"),
      s("trint", "Trint", "alternativeOffers"),
      s("sonix", "Sonix", "alternativeOffers"),
      s("chatgpt", "ChatGPT", "alternativeOffers"),
      s("zoom", "Zoom", "alternativeOffers"),
      s("tldv", "TLDV", "alternativeOffers"),
      s("videoask", "VideoAsk", "alternativeOffers"),
      s("coloop", "CoLoop", "alternativeOffers"),
      s("other", "Others", "alternativeOffers"),
    ]
  },
  {
    id: 3, label: "The product is too expensive", question: "Which part of the software do you find most costly?", suggestions: [
      s("transcription", "Transcription", "difficultOffers"),
      s("meetingAssistant", "Meeting Assistant", "expensiveOffers"),
      s("chat", "Chat", "expensiveOffers"),
      s("translation", "Translation", "expensiveOffers"),
      s("premiumExportAddon", "Premium Export Add-On", "addonOffers"),
      s("customCategoriesInsightsAddon", "Custom Categories & Insights Add-On", "addonOffers"),
      s("recorderCustomizationAddon", "Survey Customization Add-On", "addonOffers"),
      s("meetingAssistantCustomizationAddon", "Meeting Assistant Customization Add-On", "addonOffers"),
      s("brandedMediaSharingAddon", "Branded Media Sharing Add-On", "addonOffers"),
      s("brandedShareableMediaRepositoriesAddon", "Branded Shareable Media Repositories Add-On", "addonOffers"),
      s("other", "Others", "expensiveOffers"),
    ]
  },
  {
    id: 4, label: "The product is difficult to use", question: "Which part of the software did you find most challenging to use?", suggestions: [
      s("transcription", "Transcription", "difficultOffers"),
      s("translation", "Translation", "difficultOffers"),
      s("chat", "Chat", "difficultOffers"),
      s("dataVisualizationExploreInsights", "Data Visualization/Analyze Data", "difficultOffers"),
      s("csvUploadAnalysis", "CSV Upload/Analysis", "difficultOffers"),
      s("meetingAssistant", "Meeting Assistant", "difficultOffers"),
      s("webScraper", "Web Scraper", "difficultOffers"),
      s("surveys", "Surveys", "difficultOffers"),
      s("other", "Other", "difficultOffers"),
    ]
  },
  {
    id: 5, label: "The product is not compatible with our platform", question: "What system are you using that is not compatible with Speak?", suggestions: [
      s("transcriptionAccuracy", "Transcription Accuracy", "customOffer"),
      s("chatInsightsNotUseful", "Chat/Insights not useful", "commonOffers"),
      s("meetingAssistant", "Meeting Assistant", "commonOffers"),
      s("surveys", "Surveys", "commonOffers"),
      s("translation", "Translation", "commonOffers"),
      s("csvUploadAnalysis", "CSV Upload & Analysis", "commonOffers"),
      s("other", "Other (please provide)", "commonOffers"),
    ]
  },
  {
    id: 6, label: "The product did not meet expectations", question: "In what areas did our product fail to meet your expectations?", suggestions: [
      s("transcriptionAccuracy", "Transcription Accuracy", "difficultOffers"),
      s("chatInsightsNotUseful", "Chat/Insights not useful", "difficultOffers"),
      s("meetingAssistant", "Meeting Assistant", "difficultOffers"),
      s("surveys", "Surveys", "difficultOffers"),
      s("translation", "Translation", "difficultOffers"),
      s("csvUploadAnalysis", "CSV Upload & Analysis", "difficultOffers"),
      s("other", "Other (please provide)", "difficultOffers"),
    ]
  },
  {
    id: 7, label: "Support or customer service was unsatisfactory", question: "Our team takes customer service very seriously. How did we fail you with support?", suggestions: [
      s("slowResponses", "Slow responses", "expensiveOffers"),
      s("noResponses", "No responses", "expensiveOffers"),
      s("insufficientDocumentation", "Insufficient documentation", "expensiveOffers"),
      s("responsesNotHelpful", "Responses were not helpful", "expensiveOffers"),
      s("other", "Other", "expensiveOffers"),
    ]
  },
  {
    id: 8, label: "Incompatibility with our systems", question: "Which system or platform are you currently using?", suggestions: [
      s("CRM", "CRM (Salesforce/Hubspot etc.)", "integrationOffers"),
      s("projectManagement", "Project Management (Monday, Trello etc.)", "integrationOffers"),
      s("other", "Other (please provide)", "integrationOffers"),
    ]
  },
  {
    id: 9, label: "Compliances/Security concerns", question: "Which compliance or security concerns do you have?", suggestions: [
      s("GDPR", "GDPR", "securityOffers"),
      s("ISO", "ISO", "securityOffers"),
      s("SOC-2", "SOC-2", "securityOffers"),
      s("HIPAA", "HIPAA", "securityOffers"),
      s("other", "Other (please provide)", "securityOffers"),
    ]
  },
  {
    id: "other", label: "Others", question: "Please write your reason for cancellation", suggestions: [
      s("other", "Others", "otherOffers"),
    ]
  },
];

// -- Offer Definitions --
const OFFER_DEFINITIONS: Record<string, OfferDefinition> = {
  pauseMembership: {
    name: "Pause Membership", label: "Pause Membership", labelKey: "OFFERS.PAUSE_MEMBERSHIP.LABEL",
    offer: "What if we pause your subscription for 3 months to lock in your low rates and maintain all data and functionality for when you need to return to Speak for future work?",
    descKey: "OFFERS.PAUSE_MEMBERSHIP.OFFER",
    imagePath: "/assets/media/bg/contact_support.svg",
  },
  payAsYouGo: {
    name: "Convert to Pay-As-You-Go", label: "Convert to Pay-As-You-Go", labelKey: "OFFERS.PAYG.LABEL",
    offer: "Understood! We can move your account into a pay-as-you-go plan. Your transcription costs will now be 75% higher and Chat characters 50% higher.",
    descKey: "OFFERS.PAYG.OFFER",
  },
  creditOffer1: {
    name: "Credit $10", label: "Credit $10", labelKey: "OFFERS.CREDIT_10.LABEL",
    offer: "We truly apologize for any inconvenience! We are continuing to add to our documentation and will review your account to add or update relevant documentation. As a thank you, will you accept a $10 credit that will never expire to keep your subscription?",
    descKey: "OFFERS.CREDIT_10.OFFER",
    imagePath: "/assets/media/bg/gift_card.svg", isCredit: true, creditAmount: 10, highCreditAmount: 15,
  },
  creditOffer2: {
    name: "Credit $15", label: "Credit $15", labelKey: "OFFERS.CREDIT_15.LABEL",
    offer: "How about a $15 credit to your account. This will never expire.",
    descKey: "OFFERS.CREDIT_15.OFFER",
    imagePath: "/assets/media/bg/gift_card.svg", isCredit: true, creditAmount: 15, highCreditAmount: 25,
  },
  creditOffer3: {
    name: "Credit $20", label: "Credit $20", labelKey: "OFFERS.CREDIT_20.LABEL",
    offer: "How about a $20 credit (this will never expire)?",
    descKey: "OFFERS.CREDIT_20.OFFER",
    imagePath: "/assets/media/bg/gift_card.svg", isCredit: true, creditAmount: 20, highCreditAmount: 35,
  },
  alternativeProduct: {
    name: "Speak Better than other product", label: "Speak Better than other product", labelKey: "OFFERS.ALTERNATIVE_PRODUCT.LABEL",
    offer: "Did you know that compared to this alternative, Speak has several incredible capabilities that your alternative product does not have including:\n\n\u2022 AI Translation\n\u2022 Text Analysis\n\u2022 Data Visualization\n\u2022 Meeting Assistant Customization\n\u2022 Intelligent Meeting Routing\n\u2022 Video Playback\n\u2022 White-Label Options\n\u2022 Surveys\n\u2022 Customizable summarizations\n\u2022 Zapier Integration\n\u2022 Accessible APIs\n\u2022 Web Scraper\n\u2022 Much more!\n\nInstead of using this alternative product, would you like to continue with Speak?",
    descKey: "OFFERS.ALTERNATIVE_PRODUCT.OFFER",
    imagePath: "/assets/media/bg/review_document.svg",
  },
  contactSupport: {
    name: "Contact Support", label: "Contact Support", labelKey: "OFFERS.CONTACT_SUPPORT.LABEL",
    offer: "How about if we match or even beat the pricing of this alternative?",
    imagePath: "/assets/media/bg/contact_support.svg",
  },
  documentLink: {
    name: "Review Documentation", label: "Review Documentation", labelKey: "OFFERS.DOCUMENTATION.LABEL",
    offer: "Have you reviewed our thorough Help Documentation or sent us a message on live chat for support? We encourage you to do that before cancelling.",
    imagePath: "/assets/media/bg/review_document.svg", link: "https://docs.speakai.co/help/",
  },
  trainingSession: {
    name: "Training Session", label: "Training Session", labelKey: "OFFERS.TRAINING_SESSION.LABEL",
    offer: "Are you interested in a free personalized training to walk you through this capability? If so, please use this link to find a time that works well so we can set you up for success!",
    imagePath: "/assets/media/bg/training_session.svg", link: customerSessionUrl("success-session"),
  },
  bookCancellationCall: {
    name: "Book a Save Session", label: "Book a Save Session", labelKey: "OFFERS.BOOK_SESSION.LABEL",
    offer: "We'd love to keep you. Book a call with our team, we'll personally review your account, show you what's new, and make sure Speak is working for you. No pressure, just a real conversation.",
    link: CALENDLY_CANCEL_URL,
  },
  freePremiumAddon3: {
    name: "Free 3 Months Premium Add-on", label: "Free 3 Months Premium Add-on", labelKey: "OFFERS.FREE_ADDON_3.LABEL",
    offer: "If we give you this premium add-on free for 3 months, will you continue your subscription?",
    descKey: "OFFERS.FREE_ADDON_3.OFFER",
  },
  freePremiumAddon6: {
    name: "Free 6 Months Premium Add-on", label: "Free 6 Months Premium Add-on", labelKey: "OFFERS.FREE_ADDON_6.LABEL",
    offer: "If we give you this premium add-on free for 6 months, will you continue your subscription?",
    descKey: "OFFERS.FREE_ADDON_6.OFFER",
  },
  freePremiumAddon12: {
    name: "Free 12 Months Premium Add-on", label: "Free 12 Months Premium Add-on", labelKey: "OFFERS.FREE_ADDON_12.LABEL",
    offer: "If we give you this premium add-on free for 12 months, will you continue your subscription?",
    descKey: "OFFERS.FREE_ADDON_12.OFFER",
  },
  zapierDocumentation: {
    name: "Zapier Integration", label: "Zapier Integration", labelKey: "OFFERS.ZAPIER_DOCUMENTATION.LABEL",
    offer: "Did you know that we have a robust Speak Zapier Integration (https://zapier.com/apps/speak-ai/integrations) and API (https://docs.speakai.co/) documentation that can help you connect these systems? Would you like to learn more about this integration? With that in mind, will you continue your subscription?",
    link: "https://zapier.com/apps/speak-ai/integrations",
  },
  securityDocumentation: {
    name: "Security Documentation", label: "Security Documentation", labelKey: "OFFERS.SECURITY_DOCUMENTATION.LABEL",
    offer: "Did you know that we have a robust Speak Security Documentation (https://docs.speakai.co/help/security/) that can help you understand our security measures? Would you like to learn more about this? With that in mind, will you continue your subscription?",
    link: "https://docs.speakai.co/help/security/",
  },
  cancelRequest: {
    name: "Confirm Account Cancellation", label: "Confirm Account Cancellation", labelKey: "OFFERS.CONFIRM_CANCEL.LABEL",
    desc: "Thank you for being a Speak customer. Please share any last thoughts for Speak team before you cancel your subscription. We will process your cancellation and you will see no future charges.", descKey: "OFFERS.CONFIRM_CANCEL.DESC",
    offer: "",
    imagePath: "/assets/media/bg/cancel_subscription.svg",
  },
};

// -- Offer Bundles ------------------------------------------------
const OFFER_BUNDLES: Record<string, string[]> = {
  commonOffers: ["pauseMembership", "creditOffer2", "payAsYouGo", "cancelRequest"],
  alternativeOffers: ["alternativeProduct", "contactSupport", "cancelRequest"],
  expensiveOffers: ["creditOffer1", "creditOffer2", "creditOffer3", "cancelRequest"],
  difficultOffers: ["documentLink", "trainingSession", "creditOffer2", "cancelRequest"],
  integrationOffers: ["zapierDocumentation", "cancelRequest"],
  otherOffers: ["pauseMembership", "creditOffer2", "trainingSession", "cancelRequest"],
  securityOffers: ["securityDocumentation", "contactSupport", "cancelRequest"],
  customOffer: ["contactSupport", "cancelRequest"],
  addonOffers: ["freePremiumAddon3", "freePremiumAddon6", "freePremiumAddon12", "bookCancellationCall", "cancelRequest"],
  highValueOffers: ["bookCancellationCall", "contactSupport", "cancelRequest"],
};

const STEP_LABEL_KEYS = [
  "CANCEL_SUBSCRIPTION.STEP_USAGE",
  "CANCEL_SUBSCRIPTION.STEP_REASON",
  "CANCEL_SUBSCRIPTION.STEP_DETAILS",
  "CANCEL_SUBSCRIPTION.STEP_OFFERS",
  "CANCEL_SUBSCRIPTION.STEP_RESULT",
] as const;

const STEP_LABELS = ["Usage", "Reason", "Details", "Offers", "Result"] as const;

function StepProgressBar({ currentStep }: { currentStep: number }) {
  const { t } = useTranslation();
  return (
    <div className="mb-6 flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5" role="list" aria-label={t("CANCEL_SUBSCRIPTION.STEP_INDICATOR", "Step {{current}} of {{total}}", { current: currentStep + 1, total: STEP_LABEL_KEYS.length })}>
        {STEP_LABEL_KEYS.map((_, i) => (
          <div
            key={i}
            role="listitem"
            aria-current={i === currentStep ? "step" : undefined}
            className={cn(
              "h-1.5 flex-1 rounded-full transition-colors",
              i < currentStep ? "bg-primary/40" : i === currentStep ? "bg-primary" : "bg-muted-foreground/20"
            )}
          />
        ))}
      </div>
      <p className="text-xs text-muted-foreground sm:hidden">
        {t("CANCEL_SUBSCRIPTION.STEP_INDICATOR", "Step {{current}} of {{total}}", { current: currentStep + 1, total: STEP_LABEL_KEYS.length })}
      </p>
      <div className="hidden items-center gap-1.5 sm:flex">
        {STEP_LABEL_KEYS.map((key, i) => (
          <span key={key} className={cn("flex-1 text-center text-xs", i <= currentStep ? "text-foreground font-medium" : "text-muted-foreground")}>
            {t(key, STEP_LABELS[i])}
          </span>
        ))}
      </div>
    </div>
  );
}

const PLAN_FEATURES = [
  "Unlimited transcription", "AI-powered insights & analytics", "Meeting assistant integration",
  "Team collaboration tools", "API access & integrations",
  "Priority support", "Data export & sharing",
];

// -- Pause Dialog Data --
const PAUSE_REASONS = [
  { value: "market-conditions", label: "Impacted by market conditions" },
  { value: "project-complete", label: "The current project is complete" },
  { value: "other", label: "Other reason" },
];
const PAUSE_DURATIONS = [{ value: "1", label: "1 month" }, { value: "2", label: "2 months" }, { value: "3", label: "3 months" }];
const PAUSE_QUESTIONS: Record<string, { question: string; placeholder: string }[]> = {
  "market-conditions": [
    { question: "What price range would make our platform more affordable for you?", placeholder: "Describe the price range that would work for you..." },
    { question: "Would you reconsider staying subscribed if we provided you with a personalized offer?", placeholder: "Let us know if a personalized offer would help..." },
  ],
  "project-complete": [
    { question: "Do you have any upcoming projects that you would like to use our product for?", placeholder: "Tell us about any upcoming projects..." },
  ],
  other: [
    { question: "What is the reason for pausing your subscription?", placeholder: "Share your reason for pausing..." },
  ],
};

// -- Sub-components ----------------------------------------------

function UsageCards({ data, isLoading }: { data: UsageAnalyticsData | undefined; isLoading: boolean }) {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-lg border p-4"><Skeleton className="mb-2 h-4 w-20" /><Skeleton className="h-8 w-16" /></div>
        ))}
      </div>
    );
  }
  const cards = [
    { label: "Media Files", value: data?.media?.media ?? 0, sub: data?.media?.hours ? `${data.media.hours.toFixed(1)} hours` : null, icon: FileText },
    { label: t("MENU.SURVEYS", "Surveys"), value: data?.recorder?.recorders ?? 0, sub: data?.recorder?.recordings ? `${data.recorder.recordings} responses` : null, icon: ClipboardList },
    { label: "Meeting Assistant", value: data?.meetingAssistants?.calendars ?? 0, sub: data?.meetingAssistants?.meetingAssistants ? `${data.meetingAssistants.meetingAssistants} times assistant invited` : null, icon: Bot },
    { label: "Chat", value: data?.chat?.totalChat ?? 0, sub: data?.automations ? `${data.automations} automations created` : null, icon: MessageCircle },
  ];
  return (
    <div className="grid grid-cols-2 gap-3">
      {cards.map((card) => (
        <div key={card.label} className="flex flex-col gap-1 rounded-lg border bg-card p-4" aria-label={`${card.label}: ${card.value}`}>
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><card.icon className="h-3.5 w-3.5" aria-hidden="true" />{card.label}</div>
          <div className="text-2xl font-bold">{card.value}</div>
          {card.sub && <div className="text-xs text-muted-foreground">{card.sub}</div>}
        </div>
      ))}
    </div>
  );
}

function OfferCard({ offer, creditAmount, isLast, feedback, onFeedbackChange, onAccept, onContinue, isProcessing, onKeepSpeak, suppressFeedbackBox }: {
  offer: OfferDefinition; creditAmount?: number; isLast: boolean; feedback: string;
  onFeedbackChange: (v: string) => void; onAccept: () => void; onContinue: () => void; isProcessing: boolean; onKeepSpeak: () => void;
  suppressFeedbackBox: boolean;
}) {
  const { t } = useTranslation();
  const { creditMode } = useCredits();
  const { user } = useAuth();
  const displayAmount = creditAmount ?? offer.creditAmount;
  const isCancelRequest = offer.name === "Confirm Account Cancellation";
  const displayText = offer.descKey ? t(offer.descKey, { amount: displayAmount, defaultValue: offer.desc ?? offer.description ?? offer.offer }) : (offer.desc ?? offer.description ?? offer.offer);
  const displayLabel =
    offer.isCredit && creditMode && displayAmount
      ? formatCredits(displayAmount)
      : offer.labelKey
        ? t(offer.labelKey, { amount: displayAmount, defaultValue: offer.name })
        : offer.name;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-lg border bg-card p-5">
        <div className="flex items-start gap-3">
          {offer.imagePath && <Img src={offer.imagePath} alt="" width={96} height={96} className="h-24 w-24 rounded-lg object-contain" aria-hidden="true" />}
          <div className="flex flex-1 flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-base font-semibold">{displayLabel}</span>
              {offer.isCredit && displayAmount && <Badge variant="success" size="sm">{creditMode ? formatCredits(displayAmount) : `$${displayAmount} credit`}</Badge>}
            </div>
            <p className="whitespace-pre-line text-sm text-muted-foreground">{displayText}</p>
            {offer.link && (
              <a
                href={offer.link.includes("calendly.com") ? withBookingUtms(offer.link, "cancel_offer", { userId: user?._id }) : offer.link}
                onClick={() => { if (offer.link?.includes("calendly.com")) { trackFunnel("booking", "cta_clicked", { surface: "cancel_offer", target: offer.name }); pushDataLayer("Booking", "CTA Clicked", "cancel_offer", undefined, { surface: "cancel_offer", target: offer.name }); } }}
                target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-sm text-primary hover:underline" aria-label={t("CANCEL_SUBSCRIPTION.LEARN_MORE_ARIA", "Learn more about {{offerName}}", { offerName: offer.name })}>
                {t("CANCEL_SUBSCRIPTION.LEARN_MORE", "Learn more")} <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            )}
          </div>
        </div>
      </div>
      {isLast && (
        suppressFeedbackBox ? (
          <p className="text-sm text-muted-foreground">
            {t("CANCEL_SUBSCRIPTION.FEEDBACK_ALREADY_CAPTURED", "We've noted your feedback from the previous step. Thank you.")}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <label htmlFor="cancel-feedback" className="text-sm font-medium">
              {t("CANCEL_SUBSCRIPTION.FEEDBACK_LABEL", "What could we have done better?")}
            </label>
            <Textarea id="cancel-feedback" value={feedback} onChange={(e) => onFeedbackChange(e.target.value)}
              placeholder={t("CANCEL_SUBSCRIPTION.FEEDBACK_PLACEHOLDER", "Your feedback helps us improve (optional)")} rows={3} maxLength={1000} aria-label={t("CANCEL_SUBSCRIPTION.FEEDBACK_ARIA", "Cancellation feedback")} />
            <p className="self-end text-xs text-muted-foreground">{feedback.length}/1000</p>
          </div>
        )
      )}
      <div className="flex items-center justify-between gap-3">
        {isLast && isCancelRequest ? (
          <>
            <Button type="button" variant="ghost" className="text-destructive" onClick={onContinue} disabled={isProcessing} aria-label={t("CANCEL_SUBSCRIPTION.CONFIRM_CANCEL_ARIA", "Confirm subscription cancellation")}>
              {isProcessing ? t("CANCEL_SUBSCRIPTION.CANCELLING", "Cancelling...") : t("CANCEL_SUBSCRIPTION.CONFIRM_CANCEL", "Yes, cancel my subscription")}
            </Button>
            <Button type="button" onClick={onKeepSpeak} disabled={isProcessing} aria-label={t("CANCEL_SUBSCRIPTION.KEEP_SPEAK_ARIA", "Keep Speak subscription")}>{t("CANCEL_SUBSCRIPTION.DECLINE", "Keep Speak!")}</Button>
          </>
        ) : !isLast ? (
          <>
            <Button type="button" variant="ghost" onClick={onContinue} disabled={isProcessing} aria-label={t("CANCEL_SUBSCRIPTION.SEE_NEXT_OFFER_ARIA", "Not for me, next offer")}>
              {t("CANCEL_SUBSCRIPTION.SEE_NEXT_OFFER", "Not for me, next offer")}
            </Button>
            <Button type="button" onClick={onAccept} disabled={isProcessing} aria-label={t("CANCEL_SUBSCRIPTION.ACCEPT_OFFER_ARIA", "Accept offer: {{offerName}}", { offerName: offer.name })}>
              {isProcessing ? t("CANCEL_SUBSCRIPTION.PROCESSING", "Processing...") : t("CANCEL_SUBSCRIPTION.ACCEPT_OFFER", "Accept Offer")}
            </Button>
          </>
        ) : (
          <>
            <Button type="button" variant="ghost" className="text-destructive" onClick={onContinue} disabled={isProcessing} aria-label={t("CANCEL_SUBSCRIPTION.CONFIRM_CANCEL_ARIA", "Confirm subscription cancellation")}>
              {isProcessing ? t("CANCEL_SUBSCRIPTION.CANCELLING", "Cancelling...") : t("CANCEL_SUBSCRIPTION.CONFIRM_CANCEL", "Yes, cancel my subscription")}
            </Button>
            <Button type="button" onClick={onKeepSpeak} disabled={isProcessing} aria-label={t("CANCEL_SUBSCRIPTION.KEEP_SPEAK_ARIA", "Keep Speak subscription")}>{t("CANCEL_SUBSCRIPTION.DECLINE", "Keep Speak!")}</Button>
          </>
        )}
      </div>
    </div>
  );
}

function PauseDialog({ open, onClose, onSuccess, companyId }: {
  open: boolean; onClose: () => void; onSuccess: () => void; companyId: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const pauseMutation = usePauseSubscription();
  const [duration, setDuration] = useState("1");
  const [reason, setReason] = useState("");
  const [answers, setAnswers] = useState<Record<number, string>>({});

  const translatedDurations = PAUSE_DURATIONS.map((d) => ({ value: d.value, label: t(`CANCEL_SUBSCRIPTION.PAUSE_DURATIONS.${d.value}`, d.label) }));
  const translatedReasons = PAUSE_REASONS.map((r) => ({ value: r.value, label: t(`CANCEL_SUBSCRIPTION.PAUSE_REASONS.${r.value}`, r.label) }));
  const questions = reason ? (PAUSE_QUESTIONS[reason] ?? []) : [];
  const isValid = useMemo(() => {
    if (!reason || !duration) return false;
    return questions.every((_, idx) => (answers[idx] ?? "").trim().length >= 10);
  }, [reason, duration, questions, answers]);

  const reset = useCallback(() => { setDuration("1"); setReason(""); setAnswers({}); }, []);

  async function handleSubmit() {
    if (!isValid) return;
    try {
      trackFunnel("subscription", "pause", { duration, reason });
      await pauseMutation.mutateAsync({
        companyId, duration: parseInt(duration, 10),
        reason: PAUSE_REASONS.find((r) => r.value === reason)?.label ?? reason,
        questions: questions.map((q, idx) => ({ question: q.question, answer: (answers[idx] ?? "").trim().slice(0, 500) })),
      });
      toast.success(t("CANCEL_SUBSCRIPTION.PAUSE_TOAST_SUCCESS", "We have received your request to pause your subscription."));
      reset(); onSuccess();
    } catch (error) {
      Sentry.captureException(error);
      const msg = error instanceof Error && error.message.length < 150 ? error.message : t("CANCEL_SUBSCRIPTION.PAUSE_TOAST_ERROR", "Failed to pause subscription. Please try again.");
      toast.error(msg);
    }
  }

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} size="default">
      <DialogHeader><div className="flex items-center gap-2"><Pause className="h-5 w-5 text-primary" aria-hidden="true" /><h2 className="text-lg font-semibold">{t("CANCEL_SUBSCRIPTION.PAUSE_DIALOG_TITLE", "Pause Your Subscription")}</h2></div></DialogHeader>
      <DialogBody>
        <div className="flex flex-col gap-4">
          <ul className="flex flex-col gap-1.5 rounded-lg border bg-muted/50 px-4 py-3 text-xs text-muted-foreground">
            <li>{t("CANCEL_SUBSCRIPTION.PAUSE_INFO_1", "Your plan will be paused for the duration you select below.")}</li>
            <li>{t("CANCEL_SUBSCRIPTION.PAUSE_INFO_2", "You will not be charged during this time and your account will be reactivated automatically.")}</li>
            <li>{t("CANCEL_SUBSCRIPTION.PAUSE_INFO_3", "You can also reactivate your account at any time.")}</li>
            <li>{t("CANCEL_SUBSCRIPTION.PAUSE_INFO_4", "Your account will be converted to pay-as-you-go with limited features during the pause period.")}</li>
          </ul>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">{t("CANCEL_SUBSCRIPTION.PAUSE_DURATION_LABEL", "How long would you like to pause?")}</label>
            <Combobox value={duration} onChange={setDuration} options={translatedDurations} hideSearch aria-label={t("CANCEL_SUBSCRIPTION.PAUSE_DURATION_ARIA", "Pause duration")} />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">{t("CANCEL_SUBSCRIPTION.PAUSE_REASON_LABEL", "Why are you pausing?")}</label>
            <Combobox value={reason} onChange={(v) => { setReason(v); setAnswers({}); }} options={translatedReasons} hideSearch placeholder={t("CANCEL_SUBSCRIPTION.PAUSE_REASON_PLACEHOLDER", "Select a reason")} aria-label={t("CANCEL_SUBSCRIPTION.PAUSE_REASON_ARIA", "Pause reason")} />
          </div>
          {questions.map((q, idx) => {
            const translatedQuestion = t(`CANCEL_SUBSCRIPTION.PAUSE_QUESTIONS.${reason}.${idx}.QUESTION`, q.question);
            const translatedPlaceholder = t(`CANCEL_SUBSCRIPTION.PAUSE_QUESTIONS.${reason}.${idx}.PLACEHOLDER`, q.placeholder);
            return (
            <div key={idx} className="flex flex-col gap-2">
              <label className="text-sm font-medium">{translatedQuestion}<span className="text-destructive"> *</span></label>
              <Textarea value={answers[idx] ?? ""} onChange={(e) => setAnswers((prev) => ({ ...prev, [idx]: e.target.value }))}
                placeholder={translatedPlaceholder} rows={2} maxLength={500} aria-label={translatedQuestion} />
              <div className="flex items-center justify-between">
                {(answers[idx] ?? "").trim().length > 0 && (answers[idx] ?? "").trim().length < 10 && (
                  <p className="text-xs text-destructive">{t("CANCEL_SUBSCRIPTION.MIN_CHARS_HINT", "Please enter at least 10 characters ({{count}}/10)", { count: (answers[idx] ?? "").trim().length })}</p>
                )}
                <p className="ml-auto text-xs text-muted-foreground">{(answers[idx] ?? "").length}/500</p>
              </div>
            </div>
            );
          })}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => { reset(); onClose(); }} aria-label={t("CANCEL_SUBSCRIPTION.PAUSE_CANCEL_ARIA", "Cancel pause")}>{t("ACTION.CANCEL", "Cancel")}</Button>
        <Button type="button" onClick={handleSubmit} disabled={!isValid || pauseMutation.isPending} aria-label={t("CANCEL_SUBSCRIPTION.PAUSE_CONFIRM_ARIA", "Confirm pause subscription")}>
          {pauseMutation.isPending ? t("CANCEL_SUBSCRIPTION.PAUSING", "Pausing...") : t("CANCEL_SUBSCRIPTION.PAUSE_SUBSCRIPTION", "Pause Subscription")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// -- Main CancelFlow ---------------------------------------------

interface CancelFlowProps {
  open: boolean; onClose: () => void; onComplete: () => void;
  subscription: UserSubscription; companyId: string;
}

export function CancelFlow({ open, onClose, onComplete, subscription, companyId }: CancelFlowProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useAuth();
  const { show: showIntercom } = useIntercom();
  const [step, setStep] = useState(0);
  const [selectedReason, setSelectedReason] = useState<CancellationReason | null>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
  const [offerIndex, setOfferIndex] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [outcomeType, setOutcomeType] = useState<"retained" | "cancelled" | "paused" | null>(null);
  const [acceptedOffer, setAcceptedOffer] = useState<OfferDefinition | null>(null);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [otherText, setOtherText] = useState("");
  // isDesktop: loss-list card is always-expanded on desktop, a user toggle on mobile.
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true
  );
  const [mobileExpanded, setMobileExpanded] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const cancelMutation = useCancelSubscription();
  const acceptOfferMutation = useAcceptOffer();
  const usageQuery = useUsageAnalytics();

  // scales credit offer amounts in resolvedOfferDefs (separate threshold from isHighValueSubscriber bundle routing)
  const isHighTier = (subscription.cost?.total ?? 0) > 100;
  const isSubscribedActive =
    !!subscription &&
    !subscription.isExpired &&
    subscription.planId !== SubscriptionPlanId.Speak_Free_Trial &&
    subscription.planId !== SubscriptionPlanId.Speak_Pay_As_You_Go;

  const isHighValueSubscriber =
    isSubscribedActive && (
      (subscription.cost?.monthly ?? 0) >= HIGH_VALUE_COST_THRESHOLD ||
      (subscription.cost?.total ?? 0) >= HIGH_VALUE_YEARLY_THRESHOLD
    );
  const isPendingCancellation = subscription.status === SubscriptionStatus.PendingCancellation;
  const isProcessing = cancelMutation.isPending || acceptOfferMutation.isPending;

  // Auto-advance past usage summary when query fails, users must not be blocked by analytics errors
  useEffect(() => {
    if (usageQuery.isError && step === 0) {
      setStep(1);
    }
  }, [usageQuery.isError, step]);

  // Build credit amounts based on high tier
  const resolvedOfferDefs = useMemo(() => {
    if (!isHighTier) return OFFER_DEFINITIONS;
    return {
      ...OFFER_DEFINITIONS,
      creditOffer1: {
        ...OFFER_DEFINITIONS.creditOffer1,
        name: `Credit $${OFFER_DEFINITIONS.creditOffer1.highCreditAmount}`,
        label: `Credit $${OFFER_DEFINITIONS.creditOffer1.highCreditAmount}`,
        offer: `We truly apologize for any inconvenience! We are continuing to add to our documentation and will review your account to add or update relevant documentation. As a thank you, will you accept a $${OFFER_DEFINITIONS.creditOffer1.highCreditAmount} credit that will never expire to keep your subscription?`,
        creditAmount: OFFER_DEFINITIONS.creditOffer1.highCreditAmount,
      },
      creditOffer2: {
        ...OFFER_DEFINITIONS.creditOffer2,
        name: `Credit $${OFFER_DEFINITIONS.creditOffer2.highCreditAmount}`,
        label: `Credit $${OFFER_DEFINITIONS.creditOffer2.highCreditAmount}`,
        offer: `How about a $${OFFER_DEFINITIONS.creditOffer2.highCreditAmount} credit (this will never expire)?`,
        creditAmount: OFFER_DEFINITIONS.creditOffer2.highCreditAmount,
      },
      creditOffer3: {
        ...OFFER_DEFINITIONS.creditOffer3,
        name: `Credit $${OFFER_DEFINITIONS.creditOffer3.highCreditAmount}`,
        label: `Credit $${OFFER_DEFINITIONS.creditOffer3.highCreditAmount}`,
        offer: `How about a $${OFFER_DEFINITIONS.creditOffer3.highCreditAmount} credit (this will never expire)?`,
        creditAmount: OFFER_DEFINITIONS.creditOffer3.highCreditAmount,
      },
    };
  }, [isHighTier]);

  const resolvedOffers = useMemo(() => {
    if (!selectedSuggestion) return [];
    const bundleKey = isHighValueSubscriber ? "highValueOffers" : selectedSuggestion.offerBundleKey;
    const keys = OFFER_BUNDLES[bundleKey] ?? [];
    return keys.map((k) => resolvedOfferDefs[k]).filter(Boolean);
  }, [selectedSuggestion, resolvedOfferDefs, isHighValueSubscriber]);

  const currentOffer = resolvedOffers[offerIndex] ?? null;
  const isLastOffer = offerIndex === resolvedOffers.length - 1;
  const currentCreditAmount = useMemo(() => {
    if (!currentOffer?.isCredit) return undefined;
    return currentOffer.creditAmount;
  }, [currentOffer]);

  // Fall back to reason step when no offers resolve, prevents blank offer screen from unmapped suggestion keys
  useEffect(() => {
    if (step === 3 && resolvedOffers.length === 0) {
      setStep(1);
    }
  }, [step, resolvedOffers.length]);

  useEffect(() => {
    if (open) {
      trackFunnel("subscription", "cancel_flow_opened", {
        subscription_status: subscription.status,
        is_high_tier: isHighTier,
      });
    }
  }, [open]); // intentionally omit subscription/isHighTier, mount-time snapshot only

  useEffect(() => {
    if (step === 3 && currentOffer) {
      trackFunnel("subscription", "offer_viewed", {
        reason: selectedReason?.label,
        offer_name: currentOffer.name,
        offer_index: offerIndex,
      });
    }
  }, [step, offerIndex]); // fires on step entry and on each offer advance

  useEffect(() => {
    if (step === 4 && outcomeType) {
      trackFunnel("subscription", "flow_result", {
        outcome_type: outcomeType,
        reason: selectedReason?.label,
      });
    }
  }, [step, outcomeType]);

  const resetState = useCallback(() => {
    setStep(0); setSelectedReason(null); setSelectedSuggestion(null);
    setOfferIndex(0); setFeedback(""); setOutcomeType(null); setAcceptedOffer(null);
    setShowPauseDialog(false); setOtherText(""); setMobileExpanded(false);
  }, []);

  function handleClose() { resetState(); onClose(); }

  function handleKeepSpeak() {
    // removed: server endpoint /subscription/keep-speak never built, Angular's keepSpeak() is UI-only
    trackFunnel("subscription", "retain", { step, reason: selectedReason?.label, method: "keep_speak" });
    setOutcomeType("retained"); setStep(4);
  }

  async function handleCancelApi(offerAccepted: boolean) {
    try {
      const offerDesc = currentOffer ? (currentOffer.desc ?? currentOffer.description ?? currentOffer.offer ?? "") : "";
      trackFunnel("subscription", offerAccepted ? "offer_accepted" : "cancel", { reason: selectedReason?.label, offer: currentOffer?.name });
      await cancelMutation.mutateAsync({
        companyId, reason: selectedReason?.label ?? "",
        questions: selectedSuggestion ? [{
          question: selectedReason?.question ?? "",
          answer: selectedSuggestion.key === "other" ? otherText.trim().slice(0, 500) : selectedSuggestion.value,
        }] : [],
        offer: currentOffer ? [{ label: currentOffer.name, offer: offerDesc }] : [],
        feedback: feedback.trim().slice(0, 1000),
      });
      setOutcomeType(offerAccepted ? "retained" : "cancelled"); setStep(4);
    } catch (error) {
      Sentry.captureException(error);
      const msg = error instanceof Error && error.message.length < 150 ? error.message : t("CANCEL_SUBSCRIPTION.CANCEL_TOAST_ERROR", "Failed to cancel subscription. Please try again.");
      toast.error(msg);
    }
  }

  async function handleAcceptOfferApi() {
    try {
      const offerDesc = currentOffer ? (currentOffer.desc ?? currentOffer.description ?? currentOffer.offer ?? "") : "";
      await acceptOfferMutation.mutateAsync({
        companyId, reason: selectedReason?.label ?? "",
        offer: currentOffer ? [{ label: currentOffer.name, offer: offerDesc }] : [],
        feedback: feedback.trim().slice(0, 1000),
      });
      trackFunnel("subscription", "offer_accepted", { reason: selectedReason?.label, offer: currentOffer?.name });
      setAcceptedOffer(currentOffer ?? null);
      setOutcomeType("retained"); setStep(4);
    } catch (error) {
      Sentry.captureException(error);
      const msg = error instanceof Error && error.message.length < 150 ? error.message : t("CANCEL_SUBSCRIPTION.ACCEPT_OFFER_TOAST_ERROR", "Failed to accept offer. Please try again.");
      toast.error(msg);
    }
  }

  function handleAdvanceOffer() {
    if (isLastOffer) {
      handleCancelApi(false);
    } else {
      trackFunnel("subscription", "offer_skipped", { reason: selectedReason?.label, offer_name: currentOffer?.name, offer_index: offerIndex });
      setOfferIndex((prev) => prev + 1);
    }
  }

  function handleAcceptOffer() {
    if (!currentOffer) return;
    if (currentOffer.name === "Pause Membership") { setShowPauseDialog(true); return; }
    handleAcceptOfferApi();
  }

  function handleBookSession() {
    trackFunnel("subscription", "book_session", { reason: selectedReason?.label, step });
    trackFunnel("booking", "cta_clicked", { surface: "cancel_flow", target: "cancellation-session" });
    pushDataLayer("Booking", "CTA Clicked", "cancel_flow", undefined, { surface: "cancel_flow", target: "cancellation-session" });
    window.open(
      withBookingUtms(CALENDLY_CANCEL_URL, "cancel_flow", { userId: user?._id }),
      "_blank",
      "noopener,noreferrer",
    );
  }

  function handleSelectReason(value: string) {
    const reason = CANCELLATION_REASONS.find((r) => String(r.id) === value);
    if (reason) {
      setSelectedReason(reason);
      trackFunnel("subscription", "reason_selected", { reason: reason.label, reason_id: String(reason.id) });
    }
  }

  const reasonOptions = CANCELLATION_REASONS.map((r) => ({ value: String(r.id), label: t(`CANCEL_SUBSCRIPTION.REASONS.${r.id}.LABEL`, r.label) }));

  // RevenueCat subscribers must cancel via mobile app
  if (subscription.paymentSource === PaymentSourceType.Revenuecat) {
    return (
      <Dialog open={open} onClose={handleClose}>
        <DialogHeader><h2 className="text-lg font-semibold">{t("CANCEL_SUBSCRIPTION.MOBILE_SUBSCRIPTION")}</h2></DialogHeader>
        <DialogBody>
          <p className="text-sm text-muted-foreground">{t("CANCEL_SUBSCRIPTION.MOBILE_BODY", "Your subscription is managed through the mobile app. To cancel or make changes, please use the subscription settings on your device.")}</p>
        </DialogBody>
        <DialogFooter>
          <Button type="button" onClick={handleClose} aria-label={t("ACTION.CLOSE", "Close")}>{t("ACTION.CLOSE", "Close")}</Button>
        </DialogFooter>
      </Dialog>
    );
  }

  // Pending cancellation -- resume flow
  if (isPendingCancellation) {
    return (
      <Dialog open={open} onClose={handleClose}>
        <DialogHeader><h2 className="text-lg font-semibold">{t("CANCEL_SUBSCRIPTION.PENDING")}</h2></DialogHeader>
        <DialogBody>
          <p className="text-sm text-muted-foreground">{t("CANCEL_SUBSCRIPTION.PENDING_BODY", "Your access will continue until the end of your current billing period.")}</p>
        </DialogBody>
        <DialogFooter>
          <Button type="button" onClick={handleClose} aria-label={t("ACTION.CLOSE", "Close")}>{t("ACTION.CLOSE", "Close")}</Button>
        </DialogFooter>
      </Dialog>
    );
  }

  const isOtherSelected = selectedSuggestion?.key === "other";
  const canCheckOffers = selectedSuggestion && (!isOtherSelected || otherText.trim().length >= 10);

  return (
    <>
      <Dialog open={open} onClose={handleClose} size="xl">
        <DialogHeader>
          <div>
            <h2 className="text-lg font-semibold">
              {step === 4 ? (outcomeType === "cancelled" ? t("CANCEL_SUBSCRIPTION.SORRY") : t("CANCEL_SUBSCRIPTION.THANK_YOU")) : t("CANCEL_SUBSCRIPTION.TITLE")}
            </h2>
            {step < 4 && <p className="text-sm text-muted-foreground mt-1">{t("CANCEL_SUBSCRIPTION.DESC")}</p>}
          </div>
        </DialogHeader>
        <DialogBody>
          {step < 4 && <StepProgressBar currentStep={step} />}
          <div className={cn("flex gap-6", step === 4 ? "flex-col" : "flex-col lg:flex-row")}>
            {/* Sidebar -- plan features + book session; renders full-width above main content on mobile, aside on desktop, steps 0-3 */}
            {step < 4 && (() => {
              const showLossList = isDesktop || mobileExpanded;
              return (
                <aside className="flex w-full flex-col gap-3 lg:w-64 lg:shrink-0 lg:gap-4">
                  <div className="rounded-lg border bg-card p-4">
                    {isDesktop ? (
                      <h3 className="mb-3 text-sm font-semibold">{t("CANCEL_SUBSCRIPTION.SIDEBAR_TITLE", "You'll lose access to")}</h3>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setMobileExpanded((v) => !v)}
                        aria-expanded={mobileExpanded}
                        aria-controls="cancel-loss-list"
                        className="mb-1 flex w-full items-center justify-between rounded text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {t("CANCEL_SUBSCRIPTION.SIDEBAR_SUMMARY_MOBILE", "What you'll lose")}
                        <span aria-hidden="true">{mobileExpanded ? "−" : "+"}</span>
                      </button>
                    )}
                    {showLossList && (
                      <div id="cancel-loss-list">
                        <ul className="flex flex-col gap-2">
                          {usageQuery.data?.media?.media ? (
                            <li className="flex items-start gap-2 text-xs text-muted-foreground">
                              <Check className="mt-0.5 h-3 w-3 shrink-0 text-primary" aria-hidden="true" />
                              {t("CANCEL_SUBSCRIPTION.SIDEBAR_MEDIA_COUNT", "{{count}} media files", { count: usageQuery.data.media.media })}
                              {usageQuery.data.media.hours ? ` (${usageQuery.data.media.hours.toFixed(1)} hrs)` : ""}
                            </li>
                          ) : null}
                          {usageQuery.data?.chat?.totalChat ? (
                            <li className="flex items-start gap-2 text-xs text-muted-foreground">
                              <Check className="mt-0.5 h-3 w-3 shrink-0 text-primary" aria-hidden="true" />
                              {t("CANCEL_SUBSCRIPTION.SIDEBAR_INSIGHT_COUNT", "{{count}} AI insights generated", { count: usageQuery.data.chat.totalChat })}
                            </li>
                          ) : null}
                          {usageQuery.data?.meetingAssistants?.calendars ? (
                            <li className="flex items-start gap-2 text-xs text-muted-foreground">
                              <Check className="mt-0.5 h-3 w-3 shrink-0 text-primary" aria-hidden="true" />
                              {t("CANCEL_SUBSCRIPTION.SIDEBAR_CALENDAR_COUNT", "{{count}} meeting assistant calendars", { count: usageQuery.data.meetingAssistants.calendars })}
                            </li>
                          ) : null}
                          {(!usageQuery.data?.media?.media && !usageQuery.data?.chat?.totalChat && !usageQuery.data?.meetingAssistants?.calendars) &&
                            PLAN_FEATURES.map((f, idx) => (
                              <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
                                <Check className="mt-0.5 h-3 w-3 shrink-0 text-primary" aria-hidden="true" />{t(`CANCEL_SUBSCRIPTION.PLAN_FEATURES.${idx}`, f)}
                              </li>
                            ))
                          }
                        </ul>
                        {subscription.billEndDate && (
                          <p className="mt-3 border-t pt-3 text-xs text-muted-foreground">
                            {t("CANCEL_SUBSCRIPTION.PAID_THROUGH", "Paid through {{date}}, {{days}} days of access remaining.", {
                              date: format(new Date(subscription.billEndDate), "MMM dd, yyyy"),
                              days: Math.max(0, Math.ceil((new Date(subscription.billEndDate).getTime() - Date.now()) / 86400000)),
                            })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <button type="button" onClick={handleBookSession} disabled={isProcessing}
                    className="flex items-center gap-2 rounded-lg border bg-card p-4 text-left text-sm font-medium text-primary transition-colors hover:bg-accent disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={t("CANCEL_SUBSCRIPTION.BOOK_SESSION_ARIA", "Book a save session with our team")}>
                    <Calendar className="h-4 w-4" aria-hidden="true" />{t("CANCEL_SUBSCRIPTION.BOOK_SESSION", "Book a session")}<ExternalLink className="ml-auto h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </aside>
              );
            })()}

            {/* Main content */}
            <div className="min-w-0 flex-1">
              {step === 0 && (
                <div className="flex flex-col gap-4">
                  <p className="text-sm text-muted-foreground">{t("CANCEL_SUBSCRIPTION.USAGE_SUMMARY", "Here is a summary of what you have been using on Speak:")}</p>
                  <UsageCards data={usageQuery.data} isLoading={usageQuery.isLoading} />
                  {usageQuery.isError && <p className="text-xs text-muted-foreground">{t("CANCEL_SUBSCRIPTION.USAGE_ERROR", "Unable to load usage data. You can continue with the cancellation process.")}</p>}
                </div>
              )}

              {step === 1 && (
                <div className="flex flex-col gap-4">
                  <p className="text-sm text-muted-foreground">{t("CANCEL_SUBSCRIPTION.REASON_PROMPT", "Please tell us why you are considering cancelling:")}</p>
                  <div className="max-h-80 overflow-y-auto pr-1">
                    <RadioGroup value={selectedReason ? String(selectedReason.id) : ""} onChange={handleSelectReason}
                      options={reasonOptions} variant="default" orientation="vertical" aria-label={t("CANCEL_SUBSCRIPTION.REASONS_ARIA", "Cancellation reasons")} />
                  </div>
                </div>
              )}

              {step === 2 && selectedReason && (
                <div className="flex flex-col gap-4">
                  <p className="text-sm font-medium">{t(`CANCEL_SUBSCRIPTION.REASONS.${selectedReason.id}.QUESTION`, selectedReason.question)}</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedReason.suggestions.map((sug) => (
                      <Button key={sug.key} variant={selectedSuggestion?.key === sug.key ? "primary" : "outline"} size="sm" type="button"
                        onClick={() => {
                          const wasOther = selectedSuggestion?.key === "other";
                          setSelectedSuggestion(sug);
                          if (wasOther && sug.key !== "other") setOtherText("");
                          trackFunnel("subscription", "sub_reason_selected", { reason: selectedReason.label, sub_reason: sug.value });
                          if (sug.key !== "other") {
                            setOfferIndex(0);
                            setStep(3);
                          }
                        }} aria-label={t(`CANCEL_SUBSCRIPTION.REASONS.${selectedReason.id}.SUGGESTIONS.${sug.key}`, sug.value)} aria-pressed={selectedSuggestion?.key === sug.key}>
                        {t(`CANCEL_SUBSCRIPTION.REASONS.${selectedReason.id}.SUGGESTIONS.${sug.key}`, sug.value)}
                      </Button>
                    ))}
                  </div>
                  {isOtherSelected && (
                    <div className="flex flex-col gap-1">
                      <Textarea value={otherText} onChange={(e) => setOtherText(e.target.value)}
                        placeholder={t("CANCEL_SUBSCRIPTION.OTHER_PLACEHOLDER", "Please describe your situation...")} rows={2} maxLength={500} aria-label={t("CANCEL_SUBSCRIPTION.OTHER_ARIA", "Describe your reason")} />
                      <div className="flex items-center justify-between">
                        {otherText.trim().length > 0 && otherText.trim().length < 10 && (
                          <p className="text-xs text-destructive">{t("CANCEL_SUBSCRIPTION.MIN_CHARS_HINT", "Please enter at least 10 characters ({{count}}/10)", { count: otherText.trim().length })}</p>
                        )}
                        <p className="ml-auto text-xs text-muted-foreground">{otherText.length}/500</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {step === 3 && currentOffer && (
                <div className="flex flex-col gap-4">
                  <Badge variant="info" size="sm">{t("CANCEL_SUBSCRIPTION.OFFER_BADGE", "Offer {{n}} of {{total}}", { n: offerIndex + 1, total: resolvedOffers.length })}</Badge>
                  <OfferCard offer={currentOffer} creditAmount={currentCreditAmount} isLast={isLastOffer}
                    feedback={feedback} onFeedbackChange={setFeedback}
                    onAccept={handleAcceptOffer} suppressFeedbackBox={isOtherSelected}
                    onContinue={handleAdvanceOffer} isProcessing={isProcessing} onKeepSpeak={handleKeepSpeak} />
                </div>
              )}

              {step === 4 && (
                <div className="flex flex-col items-center gap-4 py-6 text-center">
                  {outcomeType === "retained" && (
                    <>
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
                        <CircleCheckBig className="h-8 w-8 text-success" aria-hidden="true" />
                      </div>
                      <h3 className="text-xl font-semibold">{t("CANCEL_SUBSCRIPTION.THANK_YOU", "Thank You")}</h3>
                      {acceptedOffer?.isCredit ? (
                        <p className="max-w-sm text-sm text-muted-foreground">
                          {t("CANCEL_SUBSCRIPTION.RESULT_CREDIT_APPLIED", "Your credit has been applied to your account. Thank you for staying with us!")}
                        </p>
                      ) : acceptedOffer?.name === "Book a Save Session" ? (
                        <>
                          <p className="max-w-sm text-sm text-muted-foreground">
                            {t("CANCEL_SUBSCRIPTION.RESULT_BOOK_SESSION_BODY", "Thanks for taking us up on our offer! Book your session with our team below.")}
                          </p>
                          <a href={withBookingUtms(CALENDLY_CANCEL_URL, "cancel_flow", { userId: user?._id })}
                            onClick={() => { trackFunnel("booking", "cta_clicked", { surface: "cancel_flow", target: "cancellation-session" }); pushDataLayer("Booking", "CTA Clicked", "cancel_flow", undefined, { surface: "cancel_flow", target: "cancellation-session" }); }}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                            aria-label={t("CANCEL_SUBSCRIPTION.BOOK_SESSION_CALENDLY_ARIA", "Book your save session on Calendly")}>
                            <Calendar className="h-4 w-4" aria-hidden="true" />{t("CANCEL_SUBSCRIPTION.BOOK_YOUR_SESSION", "Book Your Session")} <ExternalLink className="ml-auto h-3.5 w-3.5" aria-hidden="true" />
                          </a>
                        </>
                      ) : acceptedOffer ? (
                        <p className="max-w-sm text-sm text-muted-foreground">
                          {t("CANCEL_SUBSCRIPTION.RESULT_OFFER_ACCEPTED", "Our team will reach out to get you set up. Thank you for staying with us!")}
                        </p>
                      ) : (
                        <p className="max-w-sm text-sm text-muted-foreground">
                          {t("CANCEL_SUBSCRIPTION.RESULT_RETAINED", "Glad you're staying! Your subscription continues as normal.")}
                        </p>
                      )}
                      {acceptedOffer?.name !== "Book a Save Session" && (
                        <button type="button" onClick={() => { showIntercom(); }} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" aria-label={t("CANCEL_SUBSCRIPTION.OPEN_LIVE_CHAT_ARIA", "Open live chat")}>
                          <MessageCircle className="h-4 w-4" aria-hidden="true" />{t("CANCEL_SUBSCRIPTION.OPEN_LIVE_CHAT", "Open Live Chat")}
                        </button>
                      )}
                    </>
                  )}
                  {outcomeType === "paused" && (
                    <>
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                        <Pause className="h-8 w-8 text-primary" aria-hidden="true" />
                      </div>
                      <h3 className="text-xl font-semibold">{t("CANCEL_SUBSCRIPTION.RESULT_PAUSED_TITLE", "Subscription Paused")}</h3>
                      <p className="max-w-sm text-sm text-muted-foreground">
                        {t("CANCEL_SUBSCRIPTION.RESULT_PAUSED_BODY", "Your account is paused and your data stays safe. It will reactivate automatically at the end of the pause period, or you can resume any time from the pricing page.")}
                      </p>
                    </>
                  )}
                  {outcomeType === "cancelled" && (
                    <>
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                        <CircleX className="h-8 w-8 text-destructive" aria-hidden="true" />
                      </div>
                      <h3 className="text-xl font-semibold">{t("CANCEL_SUBSCRIPTION.SORRY")}</h3>
                      <div className="max-w-md rounded-lg border border-warning/30 bg-warning/10 p-4 text-sm text-warning">
                        {t("CANCEL_SUBSCRIPTION.RESULT_CANCELLED_BODY", "Your subscription will remain active until {{date}}. You can resume at any time from the pricing page.", {
                          date: subscription.billEndDate ? format(new Date(subscription.billEndDate), "MMM dd, yyyy") : t("CANCEL_SUBSCRIPTION.NEXT_BILLING_DATE", "your next billing date"),
                        })}
                      </div>
                      <button type="button" onClick={() => { showIntercom(); }} className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:underline" aria-label={t("CANCEL_SUBSCRIPTION.OPEN_LIVE_CHAT_ARIA", "Open live chat")}>
                        <MessageCircle className="h-4 w-4" aria-hidden="true" />{t("CANCEL_SUBSCRIPTION.STILL_HAVE_QUESTIONS", "Still have questions? Chat with us")}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          {step === 0 && (
            <>
              <Button type="button" variant="ghost" onClick={() => setStep(1)} aria-label={t("CANCEL_SUBSCRIPTION.PROCEED_TO_REASON_ARIA", "Continue to cancellation reasons")}>{t("CANCEL_SUBSCRIPTION.PROCEED_TO_REASON", "Continue")}</Button>
              <Button type="button" onClick={handleKeepSpeak} aria-label={t("CANCEL_SUBSCRIPTION.KEEP_SPEAK_ARIA", "Keep Speak subscription")}>{t("CANCEL_SUBSCRIPTION.DECLINE", "Keep Speak!")}</Button>
            </>
          )}
          {step === 1 && (
            <>
              <Button type="button" variant="ghost" onClick={() => setStep(0)} aria-label={t("CANCEL_SUBSCRIPTION.BACK_TO_USAGE_ARIA", "Go back to usage summary")}>{t("ACTION.BACK", "Back")}</Button>
              <Button type="button" variant="ghost" disabled={!selectedReason} onClick={() => { setSelectedSuggestion(null); setOtherText(""); setStep(2); }} aria-label={t("CANCEL_SUBSCRIPTION.NEXT_ARIA", "Continue to details")}>{t("ACTION.NEXT", "Next")}</Button>
              <Button type="button" onClick={handleKeepSpeak} aria-label={t("CANCEL_SUBSCRIPTION.KEEP_SPEAK_ARIA", "Keep Speak subscription")}>{t("CANCEL_SUBSCRIPTION.DECLINE", "Keep Speak!")}</Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button type="button" variant="ghost" onClick={() => { setSelectedReason(null); setSelectedSuggestion(null); setOtherText(""); setStep(1); }} aria-label={t("CANCEL_SUBSCRIPTION.BACK_TO_REASONS_ARIA", "Go back to cancellation reasons")}>{t("ACTION.BACK", "Back")}</Button>
              {canCheckOffers && <Button type="button" variant="ghost" onClick={() => { setOfferIndex(0); setStep(3); }} aria-label={t("CANCEL_SUBSCRIPTION.CHECK_OFFER_ARIA", "Check available offers")}>{t("CANCEL_SUBSCRIPTION.CHECK_OFFER", "Check for offer")}</Button>}
              <Button type="button" onClick={handleKeepSpeak} aria-label={t("CANCEL_SUBSCRIPTION.KEEP_SPEAK_ARIA", "Keep Speak subscription")}>{t("CANCEL_SUBSCRIPTION.DECLINE", "Keep Speak!")}</Button>
            </>
          )}
          {step === 4 && <Button type="button" onClick={() => { handleClose(); onComplete(); }} aria-label={outcomeType !== "cancelled" ? t("ACTION.CONTINUE", "Continue") : t("ACTION.CLOSE", "Close")}>{outcomeType !== "cancelled" ? t("ACTION.CONTINUE", "Continue") : t("ACTION.CLOSE", "Close")}</Button>}
        </DialogFooter>
      </Dialog>
      <PauseDialog open={showPauseDialog} onClose={() => setShowPauseDialog(false)}
        onSuccess={() => { setShowPauseDialog(false); setOutcomeType("paused"); setStep(4); }} companyId={companyId} />
    </>
  );
}
